<template>
  <div class="wrapper">
    <div class="h-100 overflow-hidden">
      <TheTopMenuLoggedIn :nav-open="navOpen" @toggle-nav="toggleNav" />
      <BfConsoleShell class="calcHeight layout-main" :style="{ '--bf-sidebar-w': sidebarWidth + 'px' }">
        <BfConsoleNav
          id="app-sidebar"
          v-model:collapsed="collapsed"
          v-model:open="navOpen"
          :items="navItems"
          persist
          @close="navOpen = false"
        />
        <div class="overflow-auto calcContainerWidth position-relative layout-content">
          <div class="calcContentWidth">
            <router-view />
          </div>
          <IndexNotification class="calcContentWidth" />
        </div>
      </BfConsoleShell>
    </div>
  </div>
  <IndexModal />
</template>

<script setup lang="ts">
  import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'

  import TheTopMenuLoggedIn from '@/components/common/TheTopMenuLoggedIn.vue'
  import BfConsoleShell from '@/components/ui/bahnhof/BfConsoleShell.vue'
  import BfConsoleNav from '@/components/ui/bahnhof/BfConsoleNav.vue'
  import BfIconBook from '@/components/ui/icons/BfIconBook.vue'
  import BfIconCard from '@/components/ui/icons/BfIconCard.vue'
  import BfIconDashboard from '@/components/ui/icons/BfIconDashboard.vue'
  import BfIconFeedback from '@/components/ui/icons/BfIconFeedback.vue'
  import BfIconInvoice from '@/components/ui/icons/BfIconInvoice.vue'
  import BfIconSupport from '@/components/ui/icons/BfIconSupport.vue'
  import BfIconWallet from '@/components/ui/icons/BfIconWallet.vue'
  import type { BfConsoleNavItem } from '@/typescript/types/ui-components/BfConsoleNavI'

  const IndexModal = defineAsyncComponent(() => import('@/components/modal/IndexModal.vue'))

  const route = useRoute()
  const navOpen = ref(false)

  const toggleNav = (): void => {
    navOpen.value = !navOpen.value
  }

  /* Rail state. The nav owns the toggle and the persistence (`persist`); the layout only mirrors it,
     because `.calcContainerWidth` / `.calcContentWidth` in global.scss size themselves off
     --bf-sidebar-w and would otherwise stay at the expanded width while the rail is showing. */
  const collapsed = ref(false)
  const sidebarWidth = computed<number>(() => (collapsed.value ? 64 : 220))

  /* The rows the legacy sidebar carried, in its order and to its destinations; the wording is the
     design's (`NAV` in the console sidebar, 05b) — title case throughout, and "Support Tickets"
     rather than the legacy "Tickets". `current` is authored here rather than derived inside the
     nav, which is the component's contract. */
  const navItems = computed<BfConsoleNavItem[]>(() =>
    [
      { label: 'Dashboard', to: '/dashboard', icon: BfIconDashboard },
      // `group` renders the design system's 1px hairline, the same separator the legacy sidebar drew
      // (`.item-mock`: 1px, margin 12px 16px). The label is not shown — `labelled` would turn it
      // into an uppercase caption, which the app never had. The legacy markup kept the word as
      // screen-reader-only text; the DS divider is `aria-hidden`, so that reading is lost here.
      { type: 'group' as const, label: 'Billing' },
      // Always opens on Unpaid, whichever tab the customer last left the register on.
      { label: 'My Invoices', to: { path: '/my-invoices', query: { status: 'Unpaid' } }, icon: BfIconInvoice },
      { label: 'Payment Methods', to: '/paymentmethods', icon: BfIconCard },
      { label: 'Add Funds', to: '/add-funds', icon: BfIconWallet },
      { type: 'group' as const, label: 'Support' },
      { label: 'Support Tickets', to: '/my-tickets', icon: BfIconSupport },
      {
        label: 'Knowledge Base',
        href: 'https://bahnhof.cloud/en/bahnhof-cloud-faq',
        external: true,
        icon: BfIconBook,
      },
      { label: 'Feedback', to: '/accessibility-feedback', icon: BfIconFeedback },
    ].map(item => ({
      ...item,
      current: item.to ? route.path === (typeof item.to === 'string' ? item.to : item.to.path) : false,
    })),
  )

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') navOpen.value = false
  }

  // Close the mobile sidebar drawer whenever navigation happens.
  watch(
    () => route.path,
    () => {
      navOpen.value = false
    },
  )

  onMounted(() => {
    window.addEventListener('keydown', onKeydown)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown)
  })
</script>

<style scoped lang="scss">
  .wrapper {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
  }

  .layout-main {
    position: relative;
  }

  /* BfConsoleShell paints itself --bf-bg-soft, which the card (05b) specifies for the console
     surface. The client area's pages own their own background, so the shell is kept transparent
     here and adopting the sidebar changes nothing about the page. Deliberate divergence from the
     card — two classes so it outranks the component's own single-class rule without !important. */
  .layout-main.console-layout {
    background: transparent;
  }

  /* The scrolling content column is the page's content region, so it carries the DS
     page frame's padding (03-components/12e-page-frame): the gutter, which steps to
     --bf-page-gutter-narrow at --bf-bp-sm through the token, and the trailing space.
     The 20px top is the region's, not the frame's — the design puts it on the region that
     holds the frame (`.bh-main:has(> .page-layout) { padding: var(--space-3) 0 0 }`), so the
     frame itself still takes none and BfPageHeader still owns the rhythm below it. */
  .layout-content {
    padding-block-start: var(--bf-sp-3);
    padding-inline: var(--bf-content-pad);
    padding-bottom: var(--bf-content-pad-bottom);
  }

  /* A page that carries its own frame (BfPageLayout — 03-components/12e) pays the gutter and
     the trailing space itself, so the region hands both back rather than doubling them; what
     is left is exactly the design's own placement rule, `.bh-main:has(> .page-layout) {
     padding: var(--space-3) 0 0 }`. Opting in is nothing more than rendering the frame as the
     page's root: no route flag to keep in step, and the rule disappears once the last legacy
     page has been migrated. */
  .layout-content:has(> .calcContentWidth > .page-layout) {
    padding-inline: 0;
    padding-bottom: 0;
    /* The token, not only the padding: the save bar docks at -var(--bf-content-pad-bottom)
       to cancel this padding, so leaving it at 60px parks the bar below the screen. */
    --bf-content-pad-bottom: 0px;
  }
</style>
